// StreamViewTool2.jsx
// This component allows users to stream the current view of a renderer in real-time.

// Copyright HS Analysis GmbH, 2025
// Author: Viktor Eberhardt

// Framework imports
import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";

// MUI imports
import {
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CircularProgress from "@mui/material/CircularProgress"; // Import spinner component

// HSA imports
import Backend from "../../../common/utils/Backend";
import { dataURItoBlob } from "../../../common/utils/Utils";

const StreamViewTool2 = ({ rendererDict, fileId, name }) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isStreamLive, setIsStreamLive] = useState(false);
  const [streamUrl, setStreamUrl] = useState("");
  const [streamHeadline, setStreamHeadline] = useState("Stream of HSA KIT");
  const browserUUID = React.useRef("");
  const isStreamingRef = React.useRef(isStreaming);
  const rendererRef = React.useRef(null);
  const lastUploadTimeRef = React.useRef(Date.now()); // Tracks the last successful upload time
  const HEARTBEAT_INTERVAL = 3000; // 3 seconds

  rendererRef.current = React.useMemo(() => {
    if (!rendererDict || !fileId) return null;
    return rendererDict[fileId];
  }, [rendererDict, fileId]);

  React.useEffect(() => {
    let storedBrowserUUID = localStorage.getItem("browserUUID");
    if (!storedBrowserUUID) {
      storedBrowserUUID = crypto.randomUUID();
      localStorage.setItem("browserUUID", storedBrowserUUID);
    }
    browserUUID.current = storedBrowserUUID;
    console.log("set browserUUID", browserUUID.current);
    setStreamUrl(
      (
        "https://stream.hs-analysis.com?target=" +
        storedBrowserUUID +
        "&name=" +
        name
      ).replace(/ /g, "_")
    );
    return () => {
      // Cleanup: reset the streaming state when the component unmounts
      setIsStreaming(false);
      isStreamingRef.current = false;
      setIsStreamLive(false);
    };
  }, []);

  /**
   * Captures the current content of the renderer's canvas as a JPEG image,
   * converts it to a Blob, and streams it to the backend server if streaming is active.
   * Continues to upload images at regular intervals (every 100ms) while streaming is enabled.
   * Updates the stream live state and last upload timestamp accordingly.
   */
  const uploadRendererContent = useCallback(() => {
    if (rendererRef.current && isStreamingRef.current) {
      let img = rendererRef.current.canvas.toDataURL("image/jpeg", 0.9);
      Backend.streamRenderer(dataURItoBlob(img), browserUUID.current).then(
        () => {
          if (isStreamingRef.current) {
            setIsStreamLive(true);
            lastUploadTimeRef.current = Date.now(); // Update last upload time
            setTimeout(uploadRendererContent, 100);
          }
        }
      );
    } else {
      setIsStreamLive(false);
    }
  }, [rendererRef, name]);

  /**
   * Toggles the streaming state.
   *
   * Updates the `isStreaming` state and its corresponding ref (`isStreamingRef.current`).
   * If streaming is enabled, triggers the `uploadRendererContent` function.
   */
  const toggleStream = () => {
    setIsStreaming((prev) => {
      const newState = !prev;
      isStreamingRef.current = newState; // Update the ref with the new state
      if (newState) {
        uploadRendererContent();
      }
      return newState;
    });
  };

  /**
   * Copies the current stream URL to the clipboard and displays a success snackbar notification.
   */
  const handleCopyLink = () => {
    navigator.clipboard.writeText(streamUrl);
    window.showSuccessSnackbar("Stream link copied to clipboard!");
  };

  // Heartbeat mechanism to restart uploadRendererContent if it stops
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (
        isStreamingRef.current &&
        Date.now() - lastUploadTimeRef.current > HEARTBEAT_INTERVAL
      ) {
        console.warn(
          "Heartbeat detected a stall. Restarting upload...",
          Date.now() - lastUploadTimeRef.current,
          HEARTBEAT_INTERVAL
        );
        uploadRendererContent();
      }
    }, HEARTBEAT_INTERVAL);

    return () => clearInterval(interval); // Cleanup on unmount
  }, [uploadRendererContent]);

  React.useEffect(() => {
    const encodedHeadline = encodeURIComponent(streamHeadline);
    setStreamUrl(
      `https://stream.hs-analysis.com?target=${browserUUID.current}&name=${encodedHeadline}`
    );
  }, [streamHeadline, browserUUID]);

  return (
    <div>
      <Typography variant="h6">
        <span style={{ position: "relative", top: "-6px", marginRight: "8px" }}>
          Stream
        </span>
        {isStreaming && !isStreamLive && (
          <CircularProgress size={24} style={{ marginRight: "10px" }} />
        )}
      </Typography>

      <FormControl
        fullWidth
        sx={{ m: 1, margin: "0 0 10px 0" }}
        variant="outlined"
      >
        <InputLabel htmlFor="outlined-adornment-password">
          Stream Headline
        </InputLabel>
        <OutlinedInput
          fullWidth
          type="text"
          value={streamHeadline}
          onChange={(e) => {
            setStreamHeadline(e.target.value);
          }}
          label="Stream Headline"
        />
      </FormControl>
      <FormControl fullWidth sx={{ m: 1, margin: 0 }} variant="outlined">
        <InputLabel htmlFor="outlined-adornment-password">
          Stream link
        </InputLabel>
        <OutlinedInput
          fullWidth
          type="text"
          value={streamUrl}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="copy link to clipboard"
                onClick={handleCopyLink}
                edge="end"
              >
                <ContentCopyIcon />
              </IconButton>
            </InputAdornment>
          }
          label="Stream link"
        />
      </FormControl>
      <Button
        style={{ marginTop: "5px" }}
        fullWidth
        variant="contained"
        color="primary"
        onClick={toggleStream}
      >
        {isStreaming ? <StopIcon /> : <PlayArrowIcon />}
        <span style={{ marginTop: 2 }}>
          {isStreaming ? "Stop Stream" : "Start Stream"}
        </span>
      </Button>
    </div>
  );
};

StreamViewTool2.propTypes = {
  rendererDict: PropTypes.object.isRequired,
  fileId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

export default StreamViewTool2;
